import { useState } from 'react'
import CustomTable from '../../../shared/CustomTable'
import CustomPagination from '../../../shared/CustomPagination'
import { claimGenerationColumns } from '../../../constants/EmrContants'
import { useTranslation } from 'react-i18next'
import ReusableModal from '../../../shared/ReuseableModal'
import { FaBriefcaseMedical } from 'react-icons/fa6'
import EmrDetails from '../patient-profile/EMRDetails'
import { fetchData } from '../../../helpers/fetchData'
import toast from 'react-hot-toast'
import { MdSync } from 'react-icons/md'
import { EditIcon, EyeIcon } from '../../../utils/TableActionIcons'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { setSelectPatientData } from '../../../redux/slices/BeneficiarySlice'
import { setSelectedVisitData } from '../../../redux/slices/VisitsSlice'
import ClaimGenerationFilters from './ClaimGenerationFilters'
import DropdownField from '../../../shared/DropdownField'
import ConfirmationModal from '../../../shared/ConfirmationModal'
import { navigateSecurely } from '../../../utils/secureNavigation'
import { getTabState } from '../../../hooks/useTabPersistence'
import CheckPermission from '../../../hooks/CheckPermission'

function ClaimGenerationTable({
  payload,
  setPayload,
  data,
  loading,
  refetch,
  fetchData: initialFetch,
  applyFilters,
  resetFilters,
  onPageChange
}) {
  const { t, i18n } = useTranslation()
  const { hasPermission } = CheckPermission()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [selectedEMRData, setSelectEmrData] = useState(null)
  const [processLoading, setProcessLoading] = useState(false)
  const [viewEmrLoading, setViewEmrLoading] = useState(false)
  const [openViewEmrModal, setOpenViewEmrModal] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [selectedVisitId, setSelectedVisitId] = useState(null)
  const onPageChangeHandler = (newPage, newRowsPerPage) => {
    onPageChange(newPage, newRowsPerPage)
  }

  const handleViewEmrDetails = async (emrNumber, patientId, visitId) => {
    setViewEmrLoading(true)
    let payload = { emr_id: Number(emrNumber), patient_id: patientId, visit_id: visitId }
    let response = await fetchData('POST', '/v3/emr/getById', payload)
    setViewEmrLoading(false)
    if (response?.data) {
      setOpenViewEmrModal(true)
      setSelectEmrData(response?.data)
    } else {
      setSelectEmrData(null)
    }
  }

  const processClaim = async () => {
    if (!hasPermission('cliamgeneration', 'update')) return
    setProcessLoading(true)
    try {
      const res = await fetchData('POST', 'v3/invoices/processClaim', {
        visit_id: selectedVisitId
      })
      if (res) {
        toast.success(res?.message || t('Claim generated successfully'))
        if (refetch) {
          refetch()
        } else if (initialFetch) {
          initialFetch()
        }
      }
    } catch (e) {
      console.log(e)
      toast.error(e?.message || t('Failed to process claim'))
    } finally {
      setProcessLoading(false)
      setShowConfirmModal(false)
      setSelectedVisitId(null)
    }
  }

  const handleProcessClaimClick = visitId => {
    setSelectedVisitId(visitId)
    setShowConfirmModal(true)
  }
  const tableActionsTemplate = rowData => (
    <div className={`flex items-center`}>
      {hasPermission('cliamgeneration', 'update') && (
        <MdSync
          size={22}
          className='text-primary cursor-pointer'
          onClick={() => handleProcessClaimClick(rowData?.visit_id)}
          title={t('ProcessClaim')}
        />
      )}

      {rowData?.emr_numbers?.length > 0 && (
        <div className='flex items-center'>
          {hasPermission('emr', 'update') && (
            <EditIcon
              title={t('EditEmr')}
              onClick={() => {
                if (!rowData?.emr_numbers?.length) {
                  return toast.error(t('No Emr id Found for this visit'))
                }
                let patientData = {
                  ...rowData?.patient,
                  visit_id: rowData?.visit_id
                }
                dispatch(setSelectPatientData(patientData))
                dispatch(setSelectedVisitData(rowData))

              const tabState = getTabState('claims_batch_flow_tabs')

                navigateSecurely(navigate, '/patient-profile/edit-emr', {
                  emr_id: rowData?.emr_numbers?.[0],
                  visitData: rowData,
                  returnTo: '/claims',
                  previousState: tabState
                })
              }}
            />
          )}

          {hasPermission('emr', 'get') && (
            <EyeIcon
              onClick={() => handleViewEmrDetails(rowData?.emr_numbers?.[0], rowData?.patient?.patient_id, rowData?.visit_id)}
            />
            
          )}
                    {/* <DropdownField
            useFormik={false}
            className=' !mb-0 i-card-dropdownfield !w-fit !h-[15px] mx-2'
            options={rowData?.reference_invoice_number?.map((data) => {
              return {
                label: data?.insurance_invoice_number || data?.corporate_invoice_number || data?.government_invoice_number,
                value: data?.invoice_number
              }
            })}
            handleChange={event => {
              let value = event?.target?.value
              navigate(`/billing/edit-invoice/${value}`, {
                state: { invoiceData: null, returnTo: '/claim-generation' }
              })
            }}
          /> */}

        </div>
      )}
    </div>
  )

  const actionsColum = {
    header: t('Actions'),
    body: tableActionsTemplate
  }

  const columns = [actionsColum, ...claimGenerationColumns(t, i18n)]
  return (
    <div>
      <div className='my-2 flex items-start justify-between'>
        <ClaimGenerationFilters
          payloadData={payload}
          setPayloadData={setPayload}
          loading={loading}
          fetchNewData={refetch || initialFetch}
          applyFilters={applyFilters}
          resetFilters={resetFilters}
        />
      </div>
      <CustomTable
        isLoading={loading || processLoading || viewEmrLoading}
        columns={columns}
        data={data?.data || []}
      />
      {data?.data?.length > 0 && (
        <CustomPagination
          currentPage={data?.page || payload?.page || 1}
          rowsPerPage={data?.perPage || payload?.perPage || 20}
          totalRecords={data?.totalCount || 0}
          totalPages={Math.ceil((data?.totalCount || 0) / (data?.perPage || payload?.perPage || 20))}
          onPageChange={onPageChangeHandler}
        />
      )}
      <ReusableModal
        position='top'
        isVisible={openViewEmrModal}
        title={t('Medical Summary')}
        HeaderIcon={FaBriefcaseMedical}
        onClose={() => {
          setOpenViewEmrModal(false)
          setSelectEmrData(null)
        }}
        className='w-full lg:w-[850px] relative'
      >
        <EmrDetails emrData={selectedEMRData} />
      </ReusableModal>

      <ConfirmationModal
        visible={showConfirmModal}
        setVisible={setShowConfirmModal}
        header={t('Generate Claim')}
        message={t('Are you sure you want to generate this claim?')}
        acceptLabel={t('Process')}
        rejectLabel={t('Cancel')}
        onAccept={processClaim}
        onReject={() => {
          setShowConfirmModal(false)
          setSelectedVisitId(null)
        }}
        isLoading={processLoading}
      />
    </div>
  )
}

export default ClaimGenerationTable
